/**
 * Book Consultation Page
 * Clean multi-step flow. Direct booking (no payment for now).
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPrice, formatDuration, formatDate, formatTime, getUserTimezone } from '../lib/utils';
import Button from '../components/common/Button';
import { Input, Textarea } from '../components/common/Input';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Container, Section } from '../components/primitives';
import { Title, Label } from '../components/primitives/Typography';
import type { Service, Category, TimeSlot, BookingFormData } from '../types';

type Step = 'service' | 'datetime' | 'details' | 'confirm';

export default function BookConsultation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<Step>('service');
  const [categories, setCategories] = useState<Category[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Generate next 14 days locally (no API call needed)
  const calendarDates = Array.from({ length: 14 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return date.toISOString().split('T')[0];
  });

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_notes: '',
  });

  const timezone = getUserTimezone();

  useEffect(() => {
    async function loadData() {
      try {
        const [catRes, svcRes] = await Promise.all([
          api.getCategories(),
          api.getServices(),
        ]);
        setCategories(catRes.categories || []);
        setServices(svcRes.services || []);

        const serviceSlug = searchParams.get('service');
        if (serviceSlug && svcRes.services) {
          const service = svcRes.services.find(s => s.slug === serviceSlug);
          if (service) {
            setSelectedService(service);
            setStep('datetime');
          }
        }
      } catch (err) {
        console.error('Failed to load services:', err);
        setError('Failed to load services. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [searchParams]);

  useEffect(() => {
    if (!selectedService || !selectedDate) return;

    async function loadSlots() {
      setLoadingSlots(true);
      try {
        const res = await api.getSlots(selectedDate, selectedService!.id, timezone);
        setSlots(res.slots || []);
        setSelectedSlot('');
      } catch (err) {
        console.error('Failed to load slots:', err);
      } finally {
        setLoadingSlots(false);
      }
    }
    loadSlots();
  }, [selectedService, selectedDate, timezone]);

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setSelectedDate('');
    setSelectedSlot('');
    setStep('datetime');
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot('');
  };

  const handleSlotSelect = (time: string) => {
    setSelectedSlot(time);
    setStep('details');
  };

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('confirm');
  };

  const handleConfirmBooking = async () => {
    if (!selectedService || !selectedDate || !selectedSlot) return;

    setSubmitting(true);
    setError(null);

    const bookingData: BookingFormData = {
      service_id: selectedService.id,
      customer_name: formData.customer_name,
      customer_email: formData.customer_email,
      customer_phone: formData.customer_phone,
      customer_notes: formData.customer_notes,
      appointment_date: selectedDate,
      appointment_time: selectedSlot,
      timezone,
    };

    try {
      const result = await api.createDirectAppointment(bookingData);
      navigate(`/booking/success?id=${result.appointment_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to book appointment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PageLoader />;

  const servicesByCategory = categories.map(cat => ({
    category: cat,
    services: services.filter(s => s.category_id === cat.id),
  })).filter(group => group.services.length > 0);

  const steps: Step[] = ['service', 'datetime', 'details', 'confirm'];
  const currentStepIndex = steps.indexOf(step);

  return (
    <main className="min-h-screen">
      <Section size="sm" className="pt-16 md:pt-24 pb-8">
        <Container narrow>
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-12">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-8 h-8 flex items-center justify-center text-small font-medium
                      ${i < currentStepIndex
                        ? 'bg-text-primary text-surface'
                        : i === currentStepIndex
                        ? 'border-2 border-text-primary text-text-primary'
                        : 'border border-border text-text-tertiary'
                      }
                    `}
                  >
                    {i < currentStepIndex ? '✓' : i + 1}
                  </div>
                  <span className="mt-2 text-caption text-text-tertiary hidden md:block">
                    {s === 'service' && 'Service'}
                    {s === 'datetime' && 'Time'}
                    {s === 'details' && 'Details'}
                    {s === 'confirm' && 'Confirm'}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-12 md:w-20 h-px mx-2 ${i < currentStepIndex ? 'bg-text-primary' : 'bg-border'}`} />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 text-red-700 text-small">
              {error}
            </div>
          )}
        </Container>
      </Section>

      {/* Step 1: Service */}
      {step === 'service' && (
        <Section size="sm" className="pt-0">
          <Container narrow>
            <Title className="mb-8">Select a service</Title>

            {servicesByCategory.length === 0 ? (
              <div className="py-12 text-center text-text-secondary">
                <p>No services available yet.</p>
                <p className="mt-2 text-small text-text-tertiary">Please check back later or contact us directly.</p>
              </div>
            ) : (
              servicesByCategory.map(({ category, services: catServices }) => (
                <div key={category.id} className="mb-10">
                  <Label className="mb-4">{category.name}</Label>
                  <div className="space-y-3">
                    {catServices.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceSelect(service)}
                        className="w-full text-left p-6 bg-white border border-border-subtle hover:border-border transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-h4 font-medium text-text-primary">{service.name}</h3>
                            <p className="mt-1 text-small text-text-secondary">{service.description}</p>
                            <p className="mt-2 text-small text-text-tertiary">
                              {formatDuration(service.duration_minutes)}
                            </p>
                          </div>
                          <span className="text-h4 font-medium text-text-primary ml-4">
                            {formatPrice(service.price_inr)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </Container>
        </Section>
      )}

      {/* Step 2: Date & Time */}
      {step === 'datetime' && selectedService && (
        <Section size="sm" className="pt-0">
          <Container narrow>
            <button
              onClick={() => setStep('service')}
              className="mb-6 text-small text-text-secondary hover:text-text-primary transition-colors"
            >
              ← Back to services
            </button>

            <div className="p-6 bg-surface-subtle mb-8">
              <div className="flex justify-between">
                <div>
                  <h3 className="text-h4 font-medium text-text-primary">{selectedService.name}</h3>
                  <p className="text-small text-text-secondary">{formatDuration(selectedService.duration_minutes)}</p>
                </div>
                <span className="text-h4 font-medium text-text-primary">
                  {formatPrice(selectedService.price_inr)}
                </span>
              </div>
            </div>

            <Title className="mb-6">Select a date</Title>

            <div className="grid grid-cols-7 gap-2 mb-10">
              {calendarDates.map((date) => {
                const d = new Date(date + 'T00:00:00');
                const isSelected = date === selectedDate;

                return (
                  <button
                    key={date}
                    onClick={() => handleDateSelect(date)}
                    className={`
                      p-3 text-center transition-colors
                      ${isSelected
                        ? 'bg-text-primary text-surface'
                        : 'bg-white border border-border hover:border-text-primary'
                      }
                    `}
                  >
                    <div className="text-caption">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className="text-h4 font-medium">{d.getDate()}</div>
                  </button>
                );
              })}
            </div>

            {selectedDate && (
              <>
                <Title className="mb-6">Select a time</Title>
                <p className="text-small text-text-tertiary mb-4">{timezone}</p>
                {loadingSlots ? (
                  <div className="py-8 text-center text-text-secondary">Loading times...</div>
                ) : slots.filter(s => s.available).length === 0 ? (
                  <div className="py-8 text-center text-text-secondary">No available times for this date</div>
                ) : (
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                    {slots.filter(s => s.available).map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => handleSlotSelect(slot.time)}
                        className={`
                          p-3 text-center text-small font-medium transition-colors
                          ${selectedSlot === slot.time
                            ? 'bg-text-primary text-surface'
                            : 'bg-white border border-border hover:border-text-primary'
                          }
                        `}
                      >
                        {formatTime(slot.time)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </Container>
        </Section>
      )}

      {/* Step 3: Details */}
      {step === 'details' && selectedService && (
        <Section size="sm" className="pt-0">
          <Container narrow>
            <button
              onClick={() => setStep('datetime')}
              className="mb-6 text-small text-text-secondary hover:text-text-primary transition-colors"
            >
              ← Back to time selection
            </button>

            <div className="p-6 bg-surface-subtle mb-8">
              <h3 className="text-h4 font-medium text-text-primary">{selectedService.name}</h3>
              <p className="mt-1 text-small text-text-secondary">
                {formatDate(selectedDate)} at {formatTime(selectedSlot)}
              </p>
              <p className="text-small text-text-tertiary">{timezone}</p>
            </div>

            <Title className="mb-8">Your details</Title>

            <form onSubmit={handleDetailsSubmit} className="space-y-6">
              <Input
                label="Full name"
                name="customer_name"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
              />

              <Input
                label="Email"
                type="email"
                name="customer_email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                required
              />

              <Input
                label="Phone"
                type="tel"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
                hint="Include country code"
              />

              <Textarea
                label="Notes (optional)"
                name="customer_notes"
                value={formData.customer_notes}
                onChange={(e) => setFormData({ ...formData, customer_notes: e.target.value })}
                placeholder="Anything specific you'd like to discuss?"
              />

              <Button type="submit" variant="primary" className="w-full">
                Continue to confirmation
              </Button>
            </form>
          </Container>
        </Section>
      )}

      {/* Step 4: Confirm */}
      {step === 'confirm' && selectedService && (
        <Section size="sm" className="pt-0">
          <Container narrow>
            <button
              onClick={() => setStep('details')}
              className="mb-6 text-small text-text-secondary hover:text-text-primary transition-colors"
            >
              ← Back to details
            </button>

            <Title className="mb-8">Confirm booking</Title>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-text-secondary">Service</span>
                <span className="font-medium text-text-primary">{selectedService.name}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-text-secondary">Duration</span>
                <span className="text-text-primary">{formatDuration(selectedService.duration_minutes)}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-text-secondary">Date</span>
                <span className="text-text-primary">{formatDate(selectedDate)}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-text-secondary">Time</span>
                <span className="text-text-primary">{formatTime(selectedSlot)} ({timezone})</span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-text-secondary">Name</span>
                <span className="text-text-primary">{formData.customer_name}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-text-secondary">Email</span>
                <span className="text-text-primary">{formData.customer_email}</span>
              </div>
              <div className="flex justify-between py-4">
                <span className="text-h4 font-medium text-text-primary">Total</span>
                <span className="text-h4 font-medium text-text-primary">{formatPrice(selectedService.price_inr)}</span>
              </div>
            </div>

            <Button
              onClick={handleConfirmBooking}
              variant="primary"
              size="lg"
              className="w-full"
              isLoading={submitting}
            >
              Confirm Booking
            </Button>

            <p className="mt-6 text-small text-text-tertiary text-center">
              You will receive a confirmation email with meeting details.
            </p>
          </Container>
        </Section>
      )}
    </main>
  );
}
